'use client'

import type { Feature } from '@cocoonspace/sdk-js/types/feature'
import { useEffect, useState } from 'react'
import { cocoonFeatures } from '@cocoonspace/shared/config/cocoon-features-config'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Plural, useLingui } from '@lingui/react/macro'
import { ChevronDownIcon, ConciergeBellIcon, XIcon } from 'lucide-react'
import Image from 'next/image'

import {
	DrawerPopover,
	DrawerPopoverContent,
	DrawerPopoverTrigger,
} from '~/components/drawer-dialog'
import { useFeaturesTrads } from '~/lib/get-features-trads'

const getDefaultFeatureState = (value?: string[]) =>
	cocoonFeatures
		.filter((feat) => feat.isSearchFilter)
		.reduce(
			(acc, feat) => {
				acc[feat.id] = value?.includes(feat.id) || false

				return acc
			},
			{} as Record<string, boolean>,
		)

export const EquipmentsFilter = ({
	features,
	onEquipmentClick,
}: {
	features: Record<string, boolean>
	onEquipmentClick: (featId: string, isChecked: boolean) => void
}) => {
	const { t } = useLingui()
	const featuresTrads = useFeaturesTrads()

	return (
		<>
			<div className='mb-4'>{t`Équipements et prestations`}</div>

			<div className='mb-4 grid grid-cols-2 gap-2 md:max-w-[400px] md:grid-cols-3'>
				{Object.entries(features).map(([featId, isChecked]) => (
					<Card
						className={cn(
							'hover:border-primary flex cursor-pointer flex-col items-center border px-4 py-1 text-center text-xs shadow-none',
							isChecked ? '!border-primary' : 'border-transparent',
						)}
						data-testid={`feature-${featId}`}
						key={featId}
						onClick={() => onEquipmentClick(featId, isChecked as boolean)}
					>
						<Image
							alt={featId}
							height='71'
							src={
								isChecked
									? `/images/features/${featId}.png`
									: `/images/features/${featId}.png`
							}
							width='89'
						/>

						{featuresTrads[featId]?.()}
					</Card>
				))}
			</div>
		</>
	)
}

export const SearchFeaturesFilter = ({
	onChange,
	value,
}: {
	onChange: (features?: string[]) => void
	value: string[] | undefined
}) => {
	const { t } = useLingui()
	const [isOpen, setIsOpen] = useState(false)

	const [features, setFeatures] = useState<Record<Feature, boolean>>(() =>
		getDefaultFeatureState(value),
	)

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setFeatures(getDefaultFeatureState(value))
	}, [isOpen, value])

	const handleConfirm = () => {
		const checkedFeatures = Object.entries(features)
			.filter(([, isChecked]) => isChecked)
			.map(([featId]) => featId as Feature)

		onChange(checkedFeatures)
	}

	return (
		<DrawerPopover
			onOpenChange={setIsOpen}
			open={isOpen}
		>
			<Card
				variant='outline'
				className='flex h-[32px] flex-row items-center rounded text-sm'
			>
				<DrawerPopoverTrigger
					className='flex flex-1 flex-row items-center gap-2 p-2'
					data-testid='open-features-popup-btn'
				>
					<ConciergeBellIcon className='text-primary' />

					<Plural
						value={value?.length ?? 0}
						_0='Services'
						one='# Service'
						other='# Services'
					/>

					{!value?.length && <ChevronDownIcon strokeWidth={1} />}
				</DrawerPopoverTrigger>

				{!!value?.length && (
					<button
						type='button'
						className='pr-2'
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							onChange(undefined)
							setIsOpen(false)
						}}
						aria-label={t`Effacer le filtre services`}
					>
						<XIcon className='text-primary' />
					</button>
				)}
			</Card>

			<DrawerPopoverContent
				className='flex max-h-[80vh] flex-col'
				sideOffset={10}
			>
				<div className='flex-1 overflow-y-auto'>
					<EquipmentsFilter
						features={features}
						onEquipmentClick={(featId, isChecked) => {
							setFeatures((features) => ({
								...features,
								[featId]: !isChecked,
							}))
						}}
					/>
				</div>

				<div className='flex flex-row justify-end gap-6'>
					<Button
						data-testid='reset-features-btn'
						size='sm'
						variant='link'
						onClick={() => {
							onChange(undefined)
							setIsOpen(false)
						}}
					>
						{t`Réinitialiser`}
					</Button>

					<Button
						data-testid='validate-features-btn'
						size='sm'
						variant='outline'
						onClick={() => {
							handleConfirm()
							setIsOpen(false)
						}}
					>
						{t`Valider`}
					</Button>
				</div>
			</DrawerPopoverContent>
		</DrawerPopover>
	)
}
